import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConnectionService } from '../../service/connection.service';
import { SwalService } from '../../service/swal.service';
import { SubscriptionService } from '../../service/subscription.service';
import { GoogleMapsLoaderService } from '../../service/google-maps-loader.service';
import { ClearZeroOnFocusDirective } from '../../utility/clear-zero-on-focus.directive';
import { PlacesAutocompleteDirective, SelectedPlace } from '../../utility/places-autocomplete.directive';
import { InfoTooltipComponent } from '../../components/info-tooltip/info-tooltip.component';
import { IconComponent } from '../../components/icon/icon.component';
import { LoadErrorComponent } from '../../components/load-error/load-error.component';
import { TimePickerComponent } from '../../components/time-picker/time-picker.component';
import { PhoneInputComponent } from '../../utility/phone-input/phone-input.component';
import { todayIso } from '../../utility/share';
import { EventTotals, eventTotals } from '../../utility/totals';
import { isQuote } from '../../utility/quote';
import { PERIOD_OPTIONS, cleanDateHint, periodYears } from '../../utility/event-period';

import { discountFields, effectivePrice } from '../../utility/service-price';

type Section = 'client' | 'trial' | 'services' | 'notes' | 'contract';

interface MissingField {
  key: string;
  section: Section | null;
  fieldId: string;
}

// Campi che il PDF del contratto stampa davvero: gli stessi richiesti per generarlo
const CONTRACT_FIELDS: { control: string; key: string; section: Section | null; fieldId: string }[] = [
  { control: 'client.name', key: 'name', section: null, fieldId: 'ce-name' },
  { control: 'event.date', key: 'date', section: null, fieldId: 'ce-date' },
  { control: 'event.location', key: 'location', section: null, fieldId: 'ce-location' },
  { control: 'client.lastname', key: 'lastname', section: null, fieldId: 'ce-lastname' },
  { control: 'client.address', key: 'address', section: 'client', fieldId: 'ce-address' },
  { control: 'client.dateOfBirth', key: 'dob', section: 'client', fieldId: 'ce-dob' },
  { control: 'client.placeOfBirth', key: 'pob', section: 'client', fieldId: 'ce-pob' },
];

@Component({
  selector: 'app-create-event',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TranslatePipe,
    ClearZeroOnFocusDirective,
    PlacesAutocompleteDirective,
    InfoTooltipComponent,
    IconComponent,
    TimePickerComponent,
    LoadErrorComponent,
    PhoneInputComponent,
  ],
  templateUrl: './create-event.component.html',
  styleUrl: './create-event.component.css',
})
export class CreateEventComponent implements OnInit {
  matrimonioForm!: FormGroup;
  show_total = false;
  show_prices = true;
  saving = false;
  readonly today = todayIso();

  openSection: Section | null = null;

  profileLoaded = false;
  private isBeautyCategory = true;
  private baseAddress = '';
  travelRatePerKm = 0;
  calculatingTravel = false;
  travelBreakdown = '';
  discountOpen = false;
  private existingEvents: any[] = [];

  constructor(
    private fb: FormBuilder,
    public router: Router,
    private route: ActivatedRoute,
    private connection: ConnectionService,
    private swal: SwalService,
    private translate: TranslateService,
    private subscription: SubscriptionService,
    private mapsLoader: GoogleMapsLoaderService,
  ) {}

  ngOnInit(): void {
    this.subscription.loadEntitlements();
    this.matrimonioForm = this.fb.group({
      client: this.fb.group({
        name: ['', Validators.required],
        lastname: [''],
        email: [''],
        phone: [''],
        address: [''],
        dateOfBirth: [''],
        placeOfBirth: [''],
      }),
      event: this.fb.group({
        location: [''],
        test_location: [''],
        location_place: [null],
        test_location_place: [null],
        date: ['', Validators.required],
        date_hint: this.fb.group({ year: [null], period: [''] }),
        test_date: [''],
        test_time: [''],
        test_cost: [null],
        time: [''],
        status: ['confirmed'],
        services: this.fb.array([]),
        show_total: [false],
        show_prices: [true],
        contract: this.fb.group({
          dateOfContract: [''],
          name: [''],
          generateContract: [false],
        }),
        note: this.fb.group({
          text: [''],
          date: [''],
          show_client: [false],
        }),
      }),
      financial: this.fb.group({
        travel_fee: this.fb.group({
          cost: [0],
          distance_km: [null],
        }),
        discount: this.fb.group({
          value: [null],
          type: ['fixed'],
          label: [''],
          include_travel: [false],
        }),
      }),
    });

    // Data e nome precompilati dal click su un giorno nel calendario
    const dateParam = this.route.snapshot.queryParamMap.get('date');
    if (dateParam) this.ctrl('event.date')?.setValue(dateParam);
    const nameParam = this.route.snapshot.queryParamMap.get('name');
    if (nameParam) this.ctrl('client.name')?.setValue(nameParam);

    this.loadProfile();
    // 'events' è cacheato: serve solo per avvisare se la data è già occupata
    this.connection.get('@events', (data: any[]) => (this.existingEvents = data ?? []), () => {});
  }

  // Listino non arrivato: errore con Riprova nella sezione Servizi, non un falso listino vuoto
  profileError = false;

  loadProfile() {
    this.connection.get(
      'auth/profile',
      (data: any) => {
        this.profileError = false;
        this.successProfile(data);
      },
      () => (this.profileError = true),
    );
  }

  // ── PREVENTIVO ────────────────────────────────────────────────────────────

  get isQuote(): boolean {
    return this.val('event.status') === 'quote';
  }

  get hasDate(): boolean {
    return !!this.val('event.date');
  }

  readonly periodOptions = PERIOD_OPTIONS;
  readonly periodYears = periodYears();

  onQuoteChange(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.ctrl('event.status')?.setValue(checked ? 'quote' : 'confirmed');
    // Il preventivo può non avere ancora una data precisa
    const date = this.ctrl('event.date');
    date?.setValidators(checked ? [] : [Validators.required]);
    date?.updateValueAndValidity();
    if (checked) this.ctrl('event.contract.generateContract')?.setValue(false);
  }

  /** Nome del cliente di un evento già confermato nella stessa data, se c'è. */
  get busyDateName(): string {
    const date = this.val('event.date');
    if (!date) return '';
    const busy = this.existingEvents.find(
      (m) => !isQuote(m) && String(m.event?.date ?? '').slice(0, 10) === date,
    );
    return busy ? [busy.client?.name, busy.client?.lastname].filter(Boolean).join(' ') : '';
  }

  private ctrl(path: string): AbstractControl | null {
    return this.matrimonioForm.get(path);
  }

  private val(path: string): any {
    return this.ctrl(path)?.value;
  }

  // ── SEZIONI ───────────────────────────────────────────────────────────────

  toggleSection(section: Section): void {
    this.openSection = this.openSection === section ? null : section;
  }

  get trialTitleKey(): string {
    return this.isBeautyCategory ? 'crea.sections.trial' : 'crea.sections.sopralluogo';
  }

  get clientFilledCount(): number {
    return ['address', 'dateOfBirth', 'placeOfBirth'].filter(
      (f) => !!this.val(`client.${f}`),
    ).length;
  }

  get trialSummary(): string {
    const d = this.val('event.test_date');
    if (!d) return '';
    const label = new Date(`${d}T00:00:00`).toLocaleDateString(this.lang, { day: 'numeric', month: 'short' });
    const time = this.val('event.test_time');
    return time ? `${label} · ${time}` : label;
  }

  get selectedServicesCount(): number {
    return this.weddingServices.controls.filter((c) => c.get('selected')?.value).length;
  }

  get notesSummary(): string {
    const t = (this.val('event.note.text') ?? '').trim();
    return t.length > 28 ? `${t.slice(0, 28)}…` : t;
  }

  private get lang(): string {
    return this.translate.currentLang || this.translate.defaultLang || 'it';
  }

  // ── PROFILO / SERVIZI ─────────────────────────────────────────────────────

  get weddingServices(): FormArray {
    return this.ctrl('event.services') as FormArray;
  }

  get travelGroup(): FormGroup {
    return this.ctrl('financial.travel_fee') as FormGroup;
  }

  get travelFee(): number {
    return +this.val('financial.travel_fee.cost') || 0;
  }

  get discountGroup(): FormGroup {
    return this.ctrl('financial.discount') as FormGroup;
  }

  get discountType(): 'fixed' | 'percent' {
    return this.val('financial.discount.type') === 'percent' ? 'percent' : 'fixed';
  }

  setDiscountType(type: 'fixed' | 'percent'): void {
    this.ctrl('financial.discount.type')?.setValue(type);
  }

  get totals(): EventTotals {
    return eventTotals(this.matrimonioForm.value);
  }

  get totalAll(): number {
    return this.totals.total;
  }

  successProfile(data: any) {
    this.profileServices = data.services ?? [];
    const servicesArray = this.weddingServices;
    for (let i = servicesArray.length - 1; i >= 0; i--) {
      if (!servicesArray.at(i).get('custom')?.value) servicesArray.removeAt(i);
    }
    const customCount = servicesArray.length;
    (data.services ?? []).forEach((s: any, idx: number) => {
      servicesArray.insert(customCount + idx,
        this.fb.group({
          name: [s.name],
          price: [effectivePrice(s)],
          full_price: [discountFields(s).full_price ?? null],
          discount_name: [discountFields(s).discount_name ?? ''],
          description: [s.description ?? ''],
          selected: [false],
          quantity: [1],
        }),
      );
    });

    this.show_total = !!data.show_total;
    this.ctrl('event.show_total')?.setValue(this.show_total);

    const cats: string[] = data.user?.professional_categories ?? [];
    this.isBeautyCategory = !cats.length || cats.some((c) => ['makeup', 'hair'].includes(c));
    const contract = data?.contract ?? data?.user?.contract ?? {};
    this.baseAddress = contract.address ?? '';
    this.travelRatePerKm = +contract.travel_rate_per_km || 0;
    this.profileLoaded = true;
  }

  // ── NUOVA VOCE DI LISTINO — pannello sopra il form, senza perdere quanto scritto ──
  private profileServices: any[] = [];
  listinoPanelOpen = false;
  savingListino = false;
  newListino: { name: string; price: number | null; description: string } = { name: '', price: null, description: '' };

  openListinoPanel(): void {
    this.newListino = { name: '', price: null, description: '' };
    this.listinoPanelOpen = true;
  }

  closeListinoPanel(): void {
    this.listinoPanelOpen = false;
  }

  /** Salva la voce nel listino del profilo e la aggiunge, già selezionata, a questo evento. */
  saveListinoItem(): void {
    const name = this.newListino.name.trim();
    if (!name) {
      this.swal.error(this.translate.instant('profile.listino.name_required'));
      return;
    }
    const item = {
      name,
      price: +(this.newListino.price ?? 0) || 0,
      description: this.newListino.description.trim(),
      default_active: true,
    };
    this.savingListino = true;
    this.connection.put(
      'auth/services',
      { services: [...this.profileServices, item] },
      () => {
        this.savingListino = false;
        this.profileServices = [...this.profileServices, item];
        this.weddingServices.push(
          this.fb.group({
            name: [item.name],
            price: [item.price],
            full_price: [null],
            discount_name: [''],
            description: [item.description],
            selected: [true],
            quantity: [1],
          }),
        );
        this.listinoPanelOpen = false;
      },
      () => {
        this.savingListino = false;
        this.swal.errorDialog(
          this.translate.instant('swal.error'),
          this.translate.instant('profile.listino.save_error'),
        );
      },
    );
  }

  addCustomService(): void {
    this.weddingServices.insert(
      0,
      this.fb.group({
        name: [''],
        price: [null],
        description: [''],
        custom: [true],
        selected: [true],
        quantity: [1],
      }),
    );
  }

  removeCustomService(i: number): void {
    this.weddingServices.removeAt(i);
  }

  isSelected(i: number): boolean {
    return !!this.weddingServices.at(i).get('selected')?.value;
  }

  quantity(i: number): number {
    return +this.weddingServices.at(i).get('quantity')?.value || 1;
  }

  changeQuantity(i: number, delta: number): void {
    this.weddingServices.at(i).get('quantity')?.setValue(Math.max(1, this.quantity(i) + delta));
  }

  subtotal(i: number): number {
    return (+this.weddingServices.at(i).get('price')?.value || 0) * this.quantity(i);
  }

  onShowTotalChange(event: Event) {
    this.show_total = (event.target as HTMLInputElement).checked;
    this.ctrl('event.show_total')?.setValue(this.show_total);
  }

  onShowPricesChange(event: Event) {
    this.show_prices = (event.target as HTMLInputElement).checked;
    this.ctrl('event.show_prices')?.setValue(this.show_prices);
  }

  onLocationPlace(place: SelectedPlace) {
    this.ctrl('event.location_place')?.setValue(place);
  }

  onTestLocationPlace(place: SelectedPlace) {
    this.ctrl('event.test_location_place')?.setValue(place);
  }

  // ── TRASFERTA (calcolo automatico, come nella scheda evento) ──────────────

  get canPro(): boolean {
    return this.subscription.canPro();
  }

  async calculateTravelFee() {
    if (!this.subscription.requirePro(this.translate.instant('crea.travel_pro'))) return;
    const t = (k: string, p?: any) => this.translate.instant(k, p);
    if (!this.baseAddress?.trim()) {
      this.swal.errorDialog(t('swal.error'), t('riepilogo.travel.no_base'));
      return;
    }
    const place = this.val('event.location_place');
    const destination =
      place?.lat != null && place?.lng != null
        ? { lat: place.lat, lng: place.lng }
        : (this.val('event.location') ?? '').trim();
    if (!destination) {
      this.swal.errorDialog(t('swal.error'), t('riepilogo.travel.no_location'));
      return;
    }

    this.calculatingTravel = true;
    try {
      await this.mapsLoader.load();
      const res = await new google.maps.DistanceMatrixService().getDistanceMatrix({
        origins: [this.baseAddress],
        destinations: [destination],
        travelMode: google.maps.TravelMode.DRIVING,
      });
      const element = res.rows?.[0]?.elements?.[0];
      if (!element || element.status !== 'OK') throw new Error('no-route');
      const roundTripKm = (element.distance.value / 1000) * 2;
      if (this.travelRatePerKm <= 0) {
        this.swal.errorDialog(t('swal.error'), t('riepilogo.travel.no_rate', { km: roundTripKm.toFixed(1) }));
        return;
      }
      this.ctrl('financial.travel_fee')?.patchValue({
        cost: Math.round(roundTripKm * this.travelRatePerKm * 100) / 100,
        distance_km: Math.round(roundTripKm * 10) / 10,
      });
      this.travelBreakdown = t('riepilogo.travel.breakdown', { km: roundTripKm.toFixed(1), rate: this.travelRatePerKm });
    } catch {
      this.swal.errorDialog(t('swal.error'), t('riepilogo.travel.error'));
    } finally {
      this.calculatingTravel = false;
    }
  }

  // ── CONTRATTO ─────────────────────────────────────────────────────────────

  get missingContractFields(): MissingField[] {
    const missing: MissingField[] = CONTRACT_FIELDS.filter((f) => !this.val(f.control)).map((f) => ({
      key: f.key,
      section: f.section,
      fieldId: f.fieldId,
    }));
    // Stessa regola della generazione nella scheda: serve almeno un servizio
    if (this.selectedServicesCount === 0) {
      missing.push({ key: 'services', section: 'services', fieldId: 'ce-services' });
    }
    return missing;
  }

  get contractDataReady(): boolean {
    return this.missingContractFields.length === 0;
  }

  get generateContract(): boolean {
    return !!this.val('event.contract.generateContract');
  }

  onGenerateContractChange(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.ctrl('event.contract.generateContract')?.setValue(checked);
  }

  goToField(f: MissingField): void {
    this.openSection = f.section;
    setTimeout(() => {
      const el = document.getElementById(f.fieldId);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.focus({ preventScroll: true });
    });
  }

  // ── SALVATAGGIO ───────────────────────────────────────────────────────────

  async submitForm() {
    if (this.saving) return;
    if (this.matrimonioForm.invalid) {
      this.swal.error(this.translate.instant('swal.fill_required'));
      this.matrimonioForm.markAllAsTouched();
      return;
    }
    if (this.generateContract && !this.contractDataReady) {
      const list = this.missingContractFields
        .map((f) => this.translate.instant(`crea.missing.${f.key}`))
        .join(', ');
      const res = await this.swal.confirm(
        this.translate.instant('crea.missing_confirm_title'),
        this.translate.instant('crea.missing_confirm_text', { list }),
        this.translate.instant('crea.create_without'),
        this.translate.instant('crea.complete_data'),
      );
      if (!res.isConfirmed) return;
    }
    const generate = this.generateContract && this.contractDataReady;
    if (!generate) this.ctrl('event.contract.generateContract')?.setValue(false);

    const payload = this.matrimonioForm.value;
    payload.event.services = payload.event.services.filter((s: any) => !s.custom || s.name?.trim());
    payload.event.date_hint = cleanDateHint(payload.event.date, payload.event.date_hint);
    payload.financial.travel_fee.cost = +payload.financial.travel_fee.cost || 0;
    payload.financial.discount.value = +payload.financial.discount.value || 0;

    this.saving = true;
    this.connection.post(
      'events',
      payload,
      (response: any) => {
        const eventId = response._id;
        if (generate) {
          // Il contratto si genera dalla scheda, con lo stesso flusso del tasto
          // Genera: scelta lingua → anteprima → Finalizza
          this.router.navigate(['/evento', eventId], { queryParams: { contratto: 'anteprima' } });
        } else if (payload.event.status === 'quote') {
          // Preventivo: dritti alla scheda, dove c'è "Invia preventivo"
          this.router.navigate(['/evento', eventId]);
        } else {
          this.swal
            .dialog(
              'success',
              this.translate.instant('swal.success'),
              this.translate.instant('swal.event_created'),
            )
            .then(() => this.router.navigate(['/evento', eventId]));
        }
      },
      (error: any) => {
        this.saving = false;
        // Il 403 da abbonamento lo gestisce l'errorInterceptor (prompt piani).
        if (error?.status === 403) return;
        this.swal.errorDialog(
          this.translate.instant('swal.error'),
          error.error?.message || this.translate.instant('swal.generic_error'),
        );
      },
    );
  }
}
